import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, CheckCircle2, XCircle, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ImportResult {
  imported: number;
  total: number;
  errors: { row: number; message: string }[];
}

interface ImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  templateEndpoint: string;
  importEndpoint: string;
  templateFilename: string;
  invalidateKey: string[];
}

export function ImportModal({
  open,
  onOpenChange,
  title,
  description,
  templateEndpoint,
  importEndpoint,
  templateFilename,
  invalidateKey,
}: ImportModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedFile(null);
      setResult(null);
    }
    onOpenChange(nextOpen);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setResult(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith(".csv")) {
      setSelectedFile(file);
      setResult(null);
    } else {
      toast({ title: "Invalid file", description: "Please drop a .csv file.", variant: "destructive" });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(templateEndpoint);
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = templateFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Download failed", description: "Could not download template.", variant: "destructive" });
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setIsImporting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await fetch(importEndpoint, { method: "POST", body: formData });
      const data = (await res.json()) as ImportResult | { error: string };
      if (!res.ok || "error" in data) {
        toast({
          title: "Import failed",
          description: "error" in data ? data.error : "Unexpected error.",
          variant: "destructive",
        });
        return;
      }
      setResult(data);
      if (data.imported > 0) {
        await queryClient.invalidateQueries({ queryKey: invalidateKey });
        toast({
          title: `Imported ${data.imported} record${data.imported !== 1 ? "s" : ""}`,
          description: data.errors.length > 0 ? `${data.errors.length} row(s) had errors — see details below.` : "All rows imported successfully.",
        });
      } else {
        toast({
          title: "Nothing imported",
          description: "All rows had errors. Check the details below.",
          variant: "destructive",
        });
      }
    } catch {
      toast({ title: "Import failed", description: "Could not connect to the server.", variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Template download */}
          <div className="flex items-center justify-between rounded-md border p-3 bg-muted/40">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span>Download the CSV template to get started</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-1" />
              Template
            </Button>
          </div>

          {/* File drop zone */}
          <div
            className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            {selectedFile ? (
              <div className="space-y-1">
                <p className="text-sm font-medium">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB · Click to change</p>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-sm font-medium">Drop your CSV file here</p>
                <p className="text-xs text-muted-foreground">or click to browse · Max 5 MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Results */}
          {result && (
            <div className="rounded-md border overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
                <span className="text-sm font-medium">Import Results</span>
                <div className="flex gap-2">
                  <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-200 gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {result.imported} imported
                  </Badge>
                  {result.errors.length > 0 && (
                    <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-200 gap-1">
                      <XCircle className="h-3 w-3" />
                      {result.errors.length} failed
                    </Badge>
                  )}
                </div>
              </div>
              {result.errors.length > 0 && (
                <div className="max-h-48 overflow-y-auto divide-y">
                  {result.errors.map((err) => (
                    <div key={err.row} className="px-3 py-2 text-xs flex gap-3">
                      <span className="text-muted-foreground shrink-0">Row {err.row}</span>
                      <span className="text-red-600">{err.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={isImporting}>
            {result ? "Close" : "Cancel"}
          </Button>
          <Button onClick={handleImport} disabled={!selectedFile || isImporting}>
            {isImporting ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
